import { useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';

import { Alert, PageContainer, Spinner } from '@shared';

import { OrderActionResultDialog } from '@/components/OrderActionResultDialog';
import { useAuth } from '@/features/auth/useAuth';
import { useLocale, useTranslations } from '@/i18n/client';
import { errorKeyOf } from '@/services/api/errors';
import { OrdersApi } from '@/services/api/orders-api';
import type { OrderFlowState, ProjectOrderDetails } from '@/services/api/types';

import {
  OrderReviewDecisionPanel,
  OrderReviewRejectionDialog,
  type DecisionOutcome,
  type PendingDecision,
} from './OrderReviewSections';
import { OrderReviewWorkspace } from './OrderReviewWorkspace';
import type { OrderReviewSupplementalContent, OrderReviewWorkflow } from './order-review-types';
import { flowStatesForOrder, normalizeOrderState } from './order-status';

function statusLabelOf(order: ProjectOrderDetails, flowStates: readonly OrderFlowState[]): string {
  const normalizedOrderState = normalizeOrderState(order.orderState);
  const currentFlowState = flowStatesForOrder(flowStates, order.orderType).find(
    (state) => normalizeOrderState(state.name) === normalizedOrderState,
  );
  if (currentFlowState) {
    return currentFlowState.displayName?.trim() || currentFlowState.name.trim();
  }

  const currentFlowStep = order.flowSteps?.find(
    (flowStep) => normalizeOrderState(flowStep.key) === normalizedOrderState,
  );

  return currentFlowStep?.displayName?.trim() || order.orderState;
}

function ReviewPagePanel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`md-review__panel ${className}`}>{children}</section>;
}

export function OrderReviewPage({
  workflow,
  renderSupplementalContent,
}: {
  readonly workflow: OrderReviewWorkflow;
  readonly renderSupplementalContent?: OrderReviewSupplementalContent;
}) {
  const { orderId } = useParams();

  return (
    <OrderReviewPageContent
      key={orderId}
      workflow={workflow}
      orderId={orderId}
      renderSupplementalContent={renderSupplementalContent}
    />
  );
}

function OrderReviewPageContent({
  workflow,
  orderId,
  renderSupplementalContent,
}: {
  workflow: OrderReviewWorkflow;
  orderId: string | undefined;
  renderSupplementalContent: OrderReviewSupplementalContent | undefined;
}) {
  const t = useTranslations('common.designReview');
  const tErrors = useTranslations('errors.api');
  const locale = useLocale();
  const { client } = useAuth();

  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [decisionOutcome, setDecisionOutcome] = useState<DecisionOutcome | null>(null);
  const [uploadResultMessage, setUploadResultMessage] = useState<string | null>(null);
  const [pendingDecision, setPendingDecision] = useState<PendingDecision>(null);

  const { page, viewer, files, decision, success, rejection } = workflow.copy;
  const orderQueryKey = ['mdenture', 'order', orderId] as const;

  const orderQuery = useQuery<ProjectOrderDetails>({
    queryKey: orderQueryKey,
    enabled: Boolean(client && orderId),
    queryFn: ({ signal }) => {
      if (!client || !orderId) {
        throw new Error('Missing authenticated order context.');
      }

      return new OrdersApi(client).getProjectOrder(orderId, signal);
    },
  });

  const flowStatesQuery = useQuery({
    queryKey: ['mdenture', 'order-flow-states', locale],
    enabled: Boolean(client && orderId),
    queryFn: ({ signal }) => {
      if (!client) {
        throw new Error('Missing authenticated order context.');
      }

      return new OrdersApi(client).getFlowStates(locale, signal);
    },
  });

  const reviewMutation = useMutation({
    mutationFn: ({ isAccepted, rejectionReason }: { isAccepted: boolean; rejectionReason?: string }) => {
      if (!client || !orderId) {
        throw new Error('Missing authenticated order context.');
      }

      return workflow.submitDecision(client, {
        orderId,
        isAccepted,
        ...(rejectionReason !== undefined ? { rejectionReason } : {}),
      });
    },
    onSuccess: (_data, variables) => {
      setDecisionOutcome(variables.isAccepted ? 'accepted' : 'rejected');
      setIsRejectDialogOpen(false);
    },
    onSettled: () => {
      setPendingDecision(null);
    },
  });

  function handleAccept(): void {
    if (!client || !orderId || !orderQuery.data || !workflow.isReviewRequired(orderQuery.data.orderState)) {
      return;
    }

    reviewMutation.reset();
    setPendingDecision('accepted');
    reviewMutation.mutate({ isAccepted: true });
  }

  function openRejectDialog(): void {
    reviewMutation.reset();
    setIsRejectDialogOpen(true);
  }

  function closeRejectDialog(): void {
    if (!reviewMutation.isPending) {
      setIsRejectDialogOpen(false);
    }
  }

  function handleReject(reason: string): void {
    if (!client || !orderId || !orderQuery.data || !workflow.isReviewRequired(orderQuery.data.orderState)) {
      return;
    }

    reviewMutation.reset();
    setPendingDecision('rejected');
    reviewMutation.mutate({ isAccepted: false, rejectionReason: reason.trim() });
  }

  if (!client || !orderId) {
    return (
      <PageContainer className="md-order-review-page">
        <ReviewPagePanel>
          <h1>{page.title}</h1>
          <Alert variant="info">{page.notAvailable}</Alert>
          <Link to="/cases">{page.backToOrders}</Link>
        </ReviewPagePanel>
      </PageContainer>
    );
  }

  if (orderQuery.isPending) {
    return (
      <PageContainer className="md-order-review-page">
        <ReviewPagePanel className="grid min-h-[14rem] place-items-center">
          <Spinner label={page.loading} />
        </ReviewPagePanel>
      </PageContainer>
    );
  }

  if (orderQuery.isError || !orderQuery.data) {
    return (
      <PageContainer className="md-order-review-page">
        <ReviewPagePanel>
          <h1>{page.title}</h1>
          <Alert variant="error">{tErrors(errorKeyOf(orderQuery.error))}</Alert>
          <Link to="/cases">{page.backToOrders}</Link>
        </ReviewPagePanel>
      </PageContainer>
    );
  }

  const order = orderQuery.data;
  const pageCopy = { ...page, ...workflow.pageCopyForOrder?.(order) };
  const isPageStateValid = workflow.isPageStateValid(order.orderState);
  const isReviewRequired = workflow.isReviewRequired(order.orderState);
  const isReviewSubmitting = reviewMutation.isPending;
  const resultMessage = decisionOutcome
    ? decisionOutcome === 'accepted'
      ? success.accepted
      : success.rejected
    : uploadResultMessage;
  const canReview = isReviewRequired && !decisionOutcome && !isReviewSubmitting;

  return (
    <PageContainer ariaLabel={pageCopy.title} className="md-order-review-page">
      <div className="mt-6 flex flex-col gap-4 max-sm:mt-4 max-sm:gap-3">
        <header className="flex flex-col items-start justify-between gap-6 rounded-[var(--zs-radius-m)] border border-[var(--md-border)] border-t-4 border-t-[var(--md-highlight)] bg-[var(--md-white)] p-6 shadow-[var(--md-shadow-soft)] max-sm:gap-4 max-sm:p-4 sm:flex-row">
          <div>
            <span className="mb-[0.55rem] block text-xs font-bold tracking-[0.06em] text-[var(--md-highlight)] uppercase">
              {pageCopy.eyebrow}
            </span>
            <h1 className="mb-0 text-[clamp(1.7rem,4vw,2.35rem)]">{pageCopy.title}</h1>
            <p className="md-order-review__description max-w-[65ch]">{pageCopy.description}</p>
          </div>
          <Link className="shrink-0 font-bold" to={`/cases/${order.id}`}>
            {page.backToOrder}
          </Link>
        </header>

        <section
          className="grid grid-cols-1 gap-px overflow-hidden rounded-[var(--zs-radius-m)] bg-[var(--md-border)] sm:grid-cols-3"
          aria-label={t('context.title')}
        >
          <div className="flex min-w-0 flex-col gap-1.5 bg-[var(--md-white)] px-[1.15rem] py-4">
            <span className="text-xs font-bold tracking-[0.04em] text-[var(--zs-muted)] uppercase">
              {t('context.orderLabel')}
            </span>
            <strong className="[overflow-wrap:anywhere] text-[var(--md-primary)]">{order.orderNumber}</strong>
          </div>
          <div className="flex min-w-0 flex-col gap-1.5 bg-[var(--md-white)] px-[1.15rem] py-4">
            <span className="text-xs font-bold tracking-[0.04em] text-[var(--zs-muted)] uppercase">
              {t('context.projectLabel')}
            </span>
            <strong className="[overflow-wrap:anywhere] text-[var(--md-primary)]">
              {order.projectName || order.customerReference}
            </strong>
          </div>
          <div className="flex min-w-0 flex-col gap-1.5 bg-[var(--md-white)] px-[1.15rem] py-4">
            <span className="text-xs font-bold tracking-[0.04em] text-[var(--zs-muted)] uppercase">
              {t('context.statusLabel')}
            </span>
            <strong className="[overflow-wrap:anywhere] text-[var(--md-primary)]">
              {statusLabelOf(order, flowStatesQuery.data ?? [])}
            </strong>
          </div>
        </section>

        {resultMessage ? null : !isPageStateValid ? (
          <ReviewPagePanel>
            <Alert variant="info">{pageCopy.notAvailable}</Alert>
            <Link to={`/cases/${order.id}`}>{pageCopy.backToOrder}</Link>
          </ReviewPagePanel>
        ) : (
          <div className="grid items-stretch gap-4 lg:grid-cols-[minmax(20rem,0.85fr)_minmax(0,1.25fr)]">
            {isReviewRequired && !resultMessage ? (
              <OrderReviewWorkspace
                order={order}
                client={client}
                copy={{ viewer, files }}
                modelFilePolicy={workflow.modelFilePolicy}
              />
            ) : null}
            {!resultMessage
              ? renderSupplementalContent?.({ order, onCompleted: setUploadResultMessage })
              : null}
            {isReviewRequired && !resultMessage ? (
              <OrderReviewDecisionPanel
                orderId={order.id}
                labels={{
                  eyebrow: decision.eyebrow,
                  title: decision.title,
                  description: decision.description,
                  accept: decision.accept,
                  accepting: decision.accepting,
                  reject: decision.reject,
                  rejecting: decision.rejecting,
                  accepted: success.accepted,
                  rejected: success.rejected,
                  backToOrder: success.backToOrder,
                }}
                decisionOutcome={decisionOutcome}
                pendingDecision={pendingDecision}
                canReview={canReview}
                errorMessage={reviewMutation.isError ? tErrors(errorKeyOf(reviewMutation.error)) : null}
                onAccept={handleAccept}
                onReject={openRejectDialog}
              />
            ) : null}
          </div>
        )}
      </div>

      {isRejectDialogOpen ? (
        <OrderReviewRejectionDialog
          labels={{
            eyebrow: rejection.eyebrow,
            title: rejection.title,
            description: rejection.description,
            reasonLabel: rejection.reasonLabel,
            reasonPlaceholder: rejection.reasonPlaceholder,
            reasonRequired: rejection.reasonRequired,
            close: rejection.close,
            cancel: rejection.cancel,
            submit: rejection.submit,
            submitting: rejection.submitting,
          }}
          isPending={reviewMutation.isPending}
          onClose={closeRejectDialog}
          onSubmit={handleReject}
        />
      ) : null}
      {resultMessage ? (
        <OrderActionResultDialog
          title={success.dialogTitle}
          message={resultMessage}
          actionLabel={success.backToOrder}
          to={`/cases/${order.id}`}
        />
      ) : null}
    </PageContainer>
  );
}
